import React from 'react';

export type ImageLoader = (args: { src: string; width: number; quality?: number }) => string;

export interface ImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'placeholder' | 'loading' | 'src'> {
  src: string | StaticImageData;
  alt: string;
  width?: number | string;
  height?: number | string;
  fill?: boolean;
  sizes?: string;
  priority?: boolean;
  placeholder?: 'blur' | 'empty' | string;
  blurDataURL?: string;
  quality?: number | string;
  unoptimized?: boolean;
  loader?: ImageLoader;
  loading?: 'lazy' | 'eager';
  onLoadingComplete?: (img: HTMLImageElement) => void;
}

export interface StaticImageData {
  src: string;
  width?: number;
  height?: number;
  blurDataURL?: string;
}

export default function AppImage({
  src,
  alt,
  width,
  height,
  className,
  style,
  fill,
  priority,
  loading,
  sizes: _sizes,
  placeholder: _placeholder,
  blurDataURL: _blurDataURL,
  quality: _quality,
  unoptimized: _unoptimized,
  loader: _loader,
  onLoadingComplete,
  onLoad,
  ...rest
}: ImageProps) {
  const resolvedSrc = typeof src === 'string' ? src : src.src;
  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    onLoadingComplete?.(e.currentTarget);
    onLoad?.(e);
  };
  if (fill) {
    const fillClass = `absolute inset-0 w-full h-full object-cover${className ? ' ' + className : ''}`;
    return (
      <img
        src={resolvedSrc}
        alt={alt}
        className={fillClass}
        style={style}
        loading={priority ? 'eager' : (loading ?? 'lazy')}
        onLoad={handleLoad}
        {...(priority ? { fetchPriority: 'high' as const } : {})}
        {...rest}
      />
    );
  }

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      width={width}
      height={height}
      className={className}
      style={style}
      loading={priority ? 'eager' : (loading ?? 'lazy')}
      onLoad={handleLoad}
      {...(priority ? { fetchPriority: 'high' as const } : {})}
      {...rest}
    />
  );
}
